import { useApiContext } from '../../../../api';
import { TpTemplateInfo } from '../../../../api/models/assets';
import { useAppDispatch } from '../../../../app/hooks';
import { TpAssetInfo } from '../../../../data/assets/interfaces';
import { modifyOpenedAsset } from '../../../../data/assets/queriedAssetsSlice';
import { TpDataContext } from '../../../../data/interfaces';
import { FORM_DATATYPES, promptAutoForm, TpAutoFormDef } from '../../../../components/auto-form-modal/AutoForm';
import { promptConfirmationBox } from '../../../../components/message-boxes/ConfirmBox';
import { showErrorMessage } from '../../../../components/message-boxes/ErrorMessage';
import { FancyButton } from '../../../../components/primitives/FancyButton';
import { serializeDashboardToYaml } from '../../../../data/dashboard-template/serialization';
import { addTemplate, modifyTemplate } from '../../../../data/templateSlice';
import { getAssetTemplateId } from '../../../../data/templateSlice.helpers';
import { useSelectDashboardTemplate } from '../../hooks/useSelectDashboardTemplate';
import { TpDashboardDefinition } from '../interfaces';

interface SaveDashboardAsTemplateButtonProps {
  dataContext: TpDataContext;
  assetInfo: TpAssetInfo;
  dashboardDef: TpDashboardDefinition;
  closeMenu?: () => void;
}

type SaveTarget = 'new' | 'current';

interface SaveTargetFormData {
  saveTarget: SaveTarget;
}

interface NewTemplateDetails {
  name: string;
  displayName: string;
  version: string;
}

function saveTargetForm(currentTemplate: TpTemplateInfo, isAssetTemplate: boolean): TpAutoFormDef {
  return {
    title: 'Save dashboard setup',
    description: isAssetTemplate
      ? 'Create a separate template or replace the dashboard template stored on this asset.'
      : 'Create a separate template or replace the template currently used by this dashboard. Updating it may affect other dashboards that use it.',
    fields: [
      {
        id: 'saveTarget',
        name: 'Save as',
        dataType: FORM_DATATYPES.MULTIPLECHOICE,
        choices: [
          { id: 'new', content: 'Create a new template' },
          {
            id: 'current',
            content: `Update current template (${currentTemplate.displayName || currentTemplate.name})`,
          },
        ],
        startContent: 'new',
        required: true,
      },
    ],
    actionButtons: [
      { id: 'cancel', content: 'Cancel', isSecondary: true },
      { id: 'continue', content: 'Continue' },
    ],
  };
}

function newTemplateDetailsForm(): TpAutoFormDef {
  return {
    title: 'Save dashboard setup',
    description:
      "Create a reusable template from this dashboard's layout, charts, and configuration.\n\nThe current dashboard state, including active data selections, is not saved.",
    fields: [
      {
        id: 'name',
        name: 'Internal Name',
        dataType: FORM_DATATYPES.STRING,
        startContent: '',
        required: true,
      },
      {
        id: 'displayName',
        name: 'Display Name',
        dataType: FORM_DATATYPES.STRING,
        startContent: '',
        required: true,
      },
      {
        id: 'version',
        name: 'Version',
        dataType: FORM_DATATYPES.STRING,
        startContent: '0.1.0',
        required: true,
      },
    ],
    actionButtons: [
      { id: 'cancel', content: 'Cancel', isSecondary: true },
      { id: 'create', content: 'Create template' },
    ],
  };
}

async function promptAutoFormOrCancel<T>(form: TpAutoFormDef, errorContext: string): Promise<T | null> {
  try {
    return (await promptAutoForm(form)) as T;
  } catch (error) {
    if (error !== undefined) {
      console.error(errorContext, error);
      showErrorMessage(error instanceof Error ? error.message : String(error));
    }
    return null;
  }
}

function reportSaveError(errorContext: string, error: unknown): void {
  console.error(errorContext, error);
  showErrorMessage(error instanceof Error ? error.message : String(error));
}

export function SaveDashboardAsTemplateButton({
  dataContext,
  assetInfo,
  dashboardDef,
  closeMenu,
}: SaveDashboardAsTemplateButtonProps) {
  const dispatch = useAppDispatch();
  const api = useApiContext();
  const selectDashboardTemplate = useSelectDashboardTemplate();

  const currentTemplate = dashboardDef.templateInfo;
  const isAssetTemplate = currentTemplate.id === getAssetTemplateId(assetInfo.id, 'dashboard');

  function serializeDashboardSetup(): string | null {
    try {
      return serializeDashboardToYaml(dataContext, dashboardDef);
    } catch (error) {
      reportSaveError('Failed to serialize the dashboard setup as a template:', error);
      return null;
    }
  }

  async function chooseSaveTarget(): Promise<SaveTarget | null> {
    if (!currentTemplate.id) return 'new';

    const choice = await promptAutoFormOrCancel<SaveTargetFormData>(
      saveTargetForm(currentTemplate, isAssetTemplate),
      'Failed to choose how to save the dashboard setup:',
    );
    return choice?.saveTarget ?? null;
  }

  async function confirmCurrentTemplateUpdate(): Promise<boolean> {
    try {
      return Boolean(
        await promptConfirmationBox({
          title: 'Update current template?',
          description: isAssetTemplate
            ? 'This will replace the dashboard template stored on this asset.'
            : 'This will replace the current template. Other dashboards using it may be affected.',
          warningAlert: true,
        }),
      );
    } catch (error) {
      if (error !== undefined) {
        reportSaveError('Failed to confirm dashboard template update:', error);
      }
      return false;
    }
  }

  /**
   * Create new draft dashboard template with the provided YAML string.
   */
  async function createNewTemplate(yamlString: string): Promise<TpTemplateInfo | null> {
    const details = await promptAutoFormOrCancel<NewTemplateDetails>(
      newTemplateDetailsForm(),
      'Failed to collect dashboard template details:',
    );
    if (!details) return null;

    const newTemplate: TpTemplateInfo = {
      ...details,
      scope: currentTemplate.scope,
      templateType: currentTemplate.templateType,
      yamlString,
      isActive: false,
      isDefault: false,
      isSystem: false,
    };

    try {
      const createdTemplate = await api.template.addTemplate(newTemplate);
      dispatch(addTemplate({ template: createdTemplate }));
      return createdTemplate;
    } catch (error) {
      reportSaveError('Failed to create a dashboard template:', error);
      return null;
    }
  }

  /**
   * Updates the current dashboard template with the provided YAML string.
   * If the template is an asset template (e.g. ETL pipeline), it updates the asset's template.
   * Otherwise, it updates the standalone template.
   * Returns the updated template info, or null if the update failed.
   */
  async function updateCurrentTemplate(yamlString: string): Promise<TpTemplateInfo | null> {
    if (!currentTemplate.id) {
      showErrorMessage('The current dashboard template cannot be updated because it has no ID.');
      return null;
    }

    try {
      if (isAssetTemplate) {
        const updatedAssetInfo = await api.asset.modifyAssetTemplate(assetInfo.id, {
          ...currentTemplate,
          yamlString,
        });
        const updatedAssetTemplate = updatedAssetInfo.assetDashboardTemplate;
        if (!updatedAssetTemplate) {
          throw new Error('The updated asset dashboard template was not returned.');
        }
        dispatch(modifyOpenedAsset({ asset: updatedAssetInfo }));
        dispatch(modifyTemplate({ template: updatedAssetTemplate }));
        return updatedAssetTemplate;
      }

      const updatedTemplate = await api.template.modifyTemplate(currentTemplate.id, { yamlString });
      dispatch(modifyTemplate({ template: updatedTemplate }));
      return updatedTemplate;
    } catch (error) {
      reportSaveError('Failed to update the current dashboard template:', error);
      return null;
    }
  }

  function selectSavedTemplate(
    template: TpTemplateInfo,
    templateId: string | null,
    saveAction: 'created' | 'updated',
  ): void {
    try {
      selectDashboardTemplate(template, dataContext, assetInfo.id, templateId);
    } catch (error) {
      console.error(`The dashboard template was ${saveAction}, but the dashboard could not be reloaded:`, error);
      showErrorMessage(
        `The dashboard template was ${saveAction}, but the dashboard could not be reloaded: ${
          error instanceof Error ? error.message : String(error)
        }`,
      );
    }
  }

  async function performSaveDashboardAsTemplate(): Promise<void> {
    closeMenu?.();

    const yamlString = serializeDashboardSetup();
    if (!yamlString) return;

    const saveTarget = await chooseSaveTarget();
    if (!saveTarget) return;

    if (saveTarget === 'current') {
      if (!(await confirmCurrentTemplateUpdate())) return;

      const updatedTemplate = await updateCurrentTemplate(yamlString);
      if (updatedTemplate) {
        selectSavedTemplate(updatedTemplate, isAssetTemplate ? null : (updatedTemplate.id ?? null), 'updated');
      }
      return;
    }

    const createdTemplate = await createNewTemplate(yamlString);
    if (createdTemplate) selectSavedTemplate(createdTemplate, createdTemplate.id ?? null, 'created');
  }

  return (
    <FancyButton iconName="floppy-disk" onClick={() => void performSaveDashboardAsTemplate()}>
      Save dashboard setup as template...
    </FancyButton>
  );
}
